import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { IMessage, IUserAdmin, IUserClient } from '@leandredev/dcbm-common-lib/dist/interfaces';
import { BaseComponent, form } from '@leandredev/dcbm-ng-lib';

import SimpleBar from 'simplebar';
import { Observable, delay, filter, map, of, switchMap, timer } from 'rxjs';

import { ScrollbarComponent } from 'src/@fury/shared/scrollbar/scrollbar.component';
import { ChatService } from 'src/app/services/chat/chat.service';
import { MatProgressBar } from '@angular/material/progress-bar';
import { IAdminChat } from '../utils';
import { interfaces } from '@leandredev/dcbm-common-lib';
import { ClientService } from 'src/app/services/client/client.service';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-chat-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class ChatDashboardComponent extends BaseComponent implements OnInit {
  public profile: IUserAdmin;

  public messageStackLength = 10;

  public chats: IAdminChat[] = [];
  public activeChat: IAdminChat;

  public replyCtrl: FormControl<string> = new FormControl('');

  @Input() closeChat: EventEmitter<boolean>;

  @ViewChild('messagesScroll', { read: ScrollbarComponent, static: true }) messagesScroll: ScrollbarComponent;
  @ViewChild('replyProgressBar') replyProgressBar: MatProgressBar;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private chatService: ChatService,
    private clientService: ClientService,
    private profileService: ProfileService,
    private snackBar: MatSnackBar
  ) {
    super();

    this.subscription.add(this.profileService.profile.pipe(map((profile) => (this.profile = profile))).subscribe());
  }

  public ngOnInit() {
    this.subscription.add(
      this.chatService.enabledChat
        .pipe(
          filter((isEnable) => isEnable),
          map(() => this.setupChat())
        )
        .subscribe()
    );
  }

  private setupChat() {
    this.chatService
      .getClient()
      .pipe(
        map((clients) => {
          this.chats = []; // fix
          clients.forEach((client) => {
            this.chats.push(this.buildChatFor(client));
          });

          this.setActiveChat(this.chats[0]);
          this.fecthUnreadMessage();
        })
      )
      .subscribe();

    timer(1000)
      .pipe(() => of(this.scrollMessageToBottom()))
      .subscribe();

    this.chatService.messageFileRequested
      .pipe(
        map((v) => {
          if (v && this.chats.length) {
            const _chat = this.chats.find((chat) => {
              return chat.profile._id === v.client;
            });

            if (_chat) {
              _chat.messages.push(v);
            }
          }
        })
      )
      .subscribe();
  }

  public fecthUnreadMessage(): Observable<any> {
    return this.chatService.getUnreadMessageOfAll(this.chats.map((chat) => chat.profile._id)).pipe(
      map((messageStacks) => {
        messageStacks.forEach((messageStack, index) => {
          const foundChat = this.chats.find((chat) => String(chat.profile._id) === String(messageStack.clientId));
          if (foundChat) {
            foundChat.unreadMessage = messageStacks[index].messages;
          }
        });
      })
    );
  }

  public buildChatFor(client: IUserClient): IAdminChat {
    const chat: IAdminChat = {
      picture: client.picture,
      name: client.name || '__Name__',
      profile: client,
      messages: [],
      lastMessageTime: null,
      lastMessage: null,
      skiped: 0,
      unreadMessage: [],
      hasPreviousMessages: false
    };

    return chat;
  }

  public showNewMessages(): void {
    this.activeChat.messages = this.activeChat.messages.concat(this.activeChat.unreadMessage);
    this.activeChat.unreadMessage = [];

    this.setMessageHasbennRead();
    this.scrollMessageToBottom();
  }

  protected getMessageOfActiveChat(): Observable<IMessage[]> {
    return this.chatService.getMessageOf(this.activeChat.profile, this.messageStackLength, this.activeChat.skiped).pipe(
      map((messages) => {
        this.setPreviousMessagesDisplayOf(messages);
        return messages;
      })
    );
  }

  public setPreviousMessagesDisplayOf(messages: IMessage[]): void {
    messages.length < this.messageStackLength ? (this.activeChat.hasPreviousMessages = false) : (this.activeChat.hasPreviousMessages = true);
  }

  public setActiveChat(chat: IAdminChat): void {
    this.activeChat = chat;

    if (chat.messages.length === 0) {
      this.getMessageOfActiveChat().subscribe((messages) => {
        messages.reverse();

        chat.messages = chat.messages.concat(
          messages.filter((message) => {
            return message.sender != 'client' || message.seenAt !== undefined;
          })
        );

        this.scrollMessageToBottom();
        this.fecthUnreadMessage().subscribe();
      });
    } else {
      this.fecthUnreadMessage().subscribe();

      this.scrollMessageToBottom();
    }
  }

  public isActive(chat: IAdminChat): boolean {
    return chat.profile._id == this.activeChat.profile._id;
  }

  public scrollMessageToBottom(): void {
    setTimeout(() => {
      const scrollBar: SimpleBar = this.messagesScroll.scrollbarRef as SimpleBar;
      const scrollElm = scrollBar.getScrollElement() as HTMLElement;
      scrollElm.scrollTop = scrollElm.scrollHeight;
    }, 10);
  }

  public send(): void {
    if (this.replyCtrl.value) {
      const messageForm = new form.Message();

      messageForm.controls.value.patchValue(this.replyCtrl.value);
      messageForm.controls.client.patchValue(this.activeChat.profile._id);
      messageForm.controls.sender.patchValue('admin');

      this.replyProgressBar.color = 'primary';
      this.replyProgressBar.mode = 'indeterminate';
      this.replyCtrl.disable();

      this.chatService
        .sendMessage(messageForm.value)
        .pipe(delay(500))
        .subscribe({
          next: (result) => {
            this.activeChat.messages.push(result);
            this.replyCtrl.reset();

            this.changeDetector.markForCheck();

            this.replyProgressBar.mode = 'determinate';
            this.replyProgressBar.value = 100;

            timer(500).subscribe(() => {
              this.replyProgressBar.value = 0;
              this.replyCtrl.enable();
              this.scrollMessageToBottom();
            });
          },
          error: (err) => {
            console.warn(err);
            this.replyProgressBar.mode = 'determinate';
            this.replyProgressBar.value = 100;

            this.replyProgressBar.color = 'warn';

            timer(1000).subscribe(() => {
              this.replyProgressBar.value = 0;
              this.replyProgressBar.color = 'primary';
              this.replyCtrl.enable();
            });
            return;
          },
          complete: () => {
            this.replyCtrl.enable();

            return;
          }
        });
    }
  }

  public fetchMessage(): void {
    this.activeChat.skiped += this.messageStackLength;

    this.getMessageOfActiveChat().subscribe((messages) => {
      messages.reverse();
      this.activeChat.messages = messages.concat(this.activeChat.messages);
    });
  }

  public setMessageHasbennRead(): void {
    const newlyReadMessages: IMessage[] = this.activeChat.messages.filter((m) => m.sender === 'client' && m.seenAt === undefined);
    this.chatService.setHasBeenRead(newlyReadMessages).subscribe(() => {
      this.chatService.getUnreadMessageCountOfAll();
    });
  }

  public validateDevice(message: interfaces.IDeviceActivationRequest): void {
    this.clientService
      .activateDevice({ id: message.client, deviceId: message.deviceId }, '', {})
      .pipe(
        switchMap(() => this.chatService.setActivated(message)),
        map((_message) => {
          if (_message) message.activated = _message.activated;
        })
      )
      .subscribe({
        complete: () => {
          // eslint-disable-next-line @typescript-eslint/quotes
          this.snackBar.open("L'appareil a été authorisé", 'x', { duration: 2000 });
        },

        error: () => this.snackBar.open('Une erreur est survenue', 'x', { duration: 2000 })
      });
  }

  refresh(): void {
    this.activeChat.messages = [];
    this.activeChat.skiped = 0;

    this.getMessageOfActiveChat().subscribe((messages) => {
      messages.reverse();

      this.activeChat.messages = this.activeChat.messages.concat(
        messages.filter((message) => {
          return message.sender != 'client' || message.seenAt;
        })
      );

      this.scrollMessageToBottom();
      this.fecthUnreadMessage().subscribe();
    });
  }
}
